import { useParams } from 'react-router'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import {
  HelpInstructionsConfigForm,
  HelpInstructionsConfigFormValues,
} from 'src/screens/administration/configuration-management/help-instructions-configuration/edit/HelpInstructionsConfigForm'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useClient } from 'urql'
import {
  queryHelpInstructionLinkById,
  updateHelpInstructionLinkById,
} from 'src/screens/administration/configuration-management/help-instructions-configuration/helpInstructionsConfigQueries'
import { Help_And_Instruction_Link_Set_Input, Mutation_Root, Query_Root } from 'src/@types/graphql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useHelpAndInstructionLinks } from 'src/service/help-and-instruction-links/useHelpAndInstructionLinks'

type OriginType = 'SAVE' | 'SAVE_AND_BACK'

const submitHelpInstructionsForm = () => {
  document.getElementById('help-instructions-config-form')?.dispatchEvent(
    new Event('submit', {
      cancelable: true,
      bubbles: true,
    }),
  )
}

export const HelpInstructionsConfigEditPage = () => {
  const { helpInstructionId } = useParams()
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()
  const { syncHelpInstructionLinks } = useHelpAndInstructionLinks()

  const [initialValues, setInitialValues] = useState<HelpInstructionsConfigFormValues | null>(null)
  const originRef = useRef<OriginType>('SAVE')

  const onBackHandler = useCallback(() => {
    navigate(ROUTES.HelpInstructionsConfigurationIndex.params({}))
  }, [navigate])

  const onSave = useCallback(() => {
    originRef.current = 'SAVE'
    submitHelpInstructionsForm()
  }, [])

  const onSaveAndBack = useCallback(() => {
    originRef.current = 'SAVE_AND_BACK'
    submitHelpInstructionsForm()
  }, [])

  const fetchHelpInstructionsLinkToEdit = useCallback(async () => {
    if (!helpInstructionId) {
      throw new Error()
    }

    const { data, error } = await urqlClient
      .query<{
        help_and_instruction_link: Query_Root['help_and_instruction_link']
      }>(queryHelpInstructionLinkById, { helpInstructionId })
      .toPromise()

    if (error || !data?.help_and_instruction_link?.[0]) {
      throw new Error('Resource not found')
    }

    const fetchedHelpInstructionsLink = data?.help_and_instruction_link[0]
    const editingHelpInstructionsLink: HelpInstructionsConfigFormValues = {
      deLabel: fetchedHelpInstructionsLink.labels['de'],
      deLink: fetchedHelpInstructionsLink.links['de'],
      enLabel: fetchedHelpInstructionsLink.labels['en'],
      enLink: fetchedHelpInstructionsLink.links['en'],
      frLabel: fetchedHelpInstructionsLink.labels['fr'],
      frLink: fetchedHelpInstructionsLink.links['fr'],
      itLabel: fetchedHelpInstructionsLink.labels['it'],
      itLink: fetchedHelpInstructionsLink.links['it'],
      key: fetchedHelpInstructionsLink.key,
      locationHints: fetchedHelpInstructionsLink.location_hints,
    }

    return { editingHelpInstructionsLink }
  }, [helpInstructionId, urqlClient])

  const handleSubmitLocal = useCallback(
    async (values: HelpInstructionsConfigFormValues) => {
      const helpInstructionsSetInput: Help_And_Instruction_Link_Set_Input = {
        key: values.key,
        labels: {
          de: values.deLabel ?? '',
          en: values.enLabel ?? '',
          fr: values.frLabel ?? '',
          it: values.itLabel ?? '',
        },
        links: {
          de: values.deLink ?? '',
          en: values.enLink ?? '',
          fr: values.frLink ?? '',
          it: values.itLink ?? '',
        },
        location_hints: values.locationHints ?? [],
      }

      const { data, error } = await urqlClient
        .mutation<{
          update_help_and_instruction_link: Mutation_Root['update_help_and_instruction_link']
        }>(updateHelpInstructionLinkById, {
          helpInstructionId: helpInstructionId,
          helpInstructionsSetInput: helpInstructionsSetInput,
        })
        .toPromise()

      if (!error && data?.update_help_and_instruction_link?.affected_rows === 1) {
        notificationService.changesSaved()
        setInitialValues(values)
        syncHelpInstructionLinks()
        if (originRef.current === 'SAVE_AND_BACK') {
          onBackHandler()
        }
      } else {
        notificationService.operationFailed()
      }
    },
    [helpInstructionId, notificationService, onBackHandler, syncHelpInstructionLinks, urqlClient],
  )

  useEffect(() => {
    fetchHelpInstructionsLinkToEdit()
      .then((res) => {
        setInitialValues(res.editingHelpInstructionsLink)
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
  }, [fetchHelpInstructionsLinkToEdit, notificationService])

  return (
    <ScreenLayout
      title={getMessage('label.configuration.help.and.instructions.edit')}
      onBack={onBackHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSaveAndBack} />
          <SaveButton origin="header" onClick={onSave} />
        </>
      }
    >
      <PageLayout>
        <>
          {initialValues && (
            <HelpInstructionsConfigForm mode="EDIT" onSubmit={handleSubmitLocal} initialValues={initialValues} />
          )}
        </>
      </PageLayout>
    </ScreenLayout>
  )
}
